import { useState, useEffect } from "react";
import { marked } from "marked";
import { useAppContext } from "../App";

interface Story {
  id: number;
  title: string;
  body: string;
  user_id: number;
  username: string;
  profile_pic_url: string | null;
  like_count: number;
  word_count: number;
  created_at: string;
  hasLiked: boolean;
}

interface Comment {
  id: number;
  submission_id: number;
  user_id: number;
  parent_id: number | null;
  body: string;
  username: string;
  profile_pic_url: string | null;
  created_at: string;
}

interface StoryPageProps {
  storyId: number;
  onBack: () => void;
}

export function StoryPage({ storyId, onBack }: StoryPageProps) {
  const { user, likes, setLikes, isAdmin, refreshData } = useAppContext();
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likeCount, setLikeCount] = useState(0);
  const [fontSize, setFontSize] = useState(() => {
    const stored = localStorage.getItem("borges-font-size");
    return stored ? parseFloat(stored) : 1;
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [saving, setSaving] = useState(false);

  // Comments state
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyText, setReplyText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  const hasLiked = likes.includes(storyId);
  const isOwner = user && story && user.id === story.user_id;
  const canDelete = isOwner || isAdmin;

  useEffect(() => {
    const fetchStory = async () => {
      try {
        const res = await fetch(`/api/story/${storyId}`);
        if (!res.ok) {
          throw new Error("Story not found");
        }
        const data = await res.json();
        setStory(data);
        setLikeCount(data.like_count);
        setEditTitle(data.title);
        setEditBody(data.body);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load story");
      } finally {
        setLoading(false);
      }
    };
    fetchStory();
  }, [storyId]);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const res = await fetch(`/api/story/${storyId}/comments`);
        if (res.ok) {
          const data = await res.json();
          setComments(data);
        }
      } catch (err) {
        console.error("Failed to load comments:", err);
      }
    };
    fetchComments();
  }, [storyId]);

  const handleLike = async () => {
    if (!user) {
      window.location.href = "/auth/login";
      return;
    }

    try {
      const res = await fetch(`/api/like/${storyId}`, { method: "POST" });
      const data = await res.json();
      setLikeCount(data.like_count);
      if (data.liked) {
        setLikes([...likes, storyId]);
      } else {
        setLikes(likes.filter((id) => id !== storyId));
      }
    } catch (err) {
      console.error("Failed to toggle like:", err);
    }
  };

  const handleSave = async () => {
    if (!editTitle.trim() || !editBody.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/story/${storyId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: editTitle, body: editBody }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to save");
      }
      const updated = await res.json();
      setStory({ ...story!, ...updated });
      setIsEditing(false);
      refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this story?")) return;
    try {
      const res = await fetch(`/api/story/${storyId}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to delete");
      }
      refreshData();
      onBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete");
    }
  };

  const handleSubmitComment = async (parentId?: number) => {
    if (!user) {
      window.location.href = "/auth/login";
      return;
    }

    const text = parentId ? replyText : newComment;
    if (!text.trim()) return;

    setSubmittingComment(true);
    try {
      const res = await fetch(`/api/story/${storyId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: text, parentId }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to post comment");
      }
      const comment = await res.json();
      setComments([...comments, comment]);
      if (parentId) {
        setReplyText("");
        setReplyingTo(null);
      } else {
        setNewComment("");
      }
    } catch (err) {
      console.error("Failed to post comment:", err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!confirm("Delete this comment?")) return;
    try {
      const res = await fetch(`/api/comment/${commentId}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to delete comment");
      }
      // Remove the comment and its children
      setComments(comments.filter(c => c.id !== commentId && c.parent_id !== commentId));
    } catch (err) {
      console.error("Failed to delete comment:", err);
    }
  };

  const changeFontSize = (delta: number) => {
    const newSize = Math.max(0.8, Math.min(1.4, fontSize + delta));
    setFontSize(newSize);
    localStorage.setItem("borges-font-size", newSize.toString());
  };

  // Build nested comment tree
  const buildCommentTree = (comments: Comment[]) => {
    const rootComments = comments.filter(c => !c.parent_id);
    const getReplies = (parentId: number): Comment[] =>
      comments.filter(c => c.parent_id === parentId);
    return { rootComments, getReplies };
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
    });
  };

  const renderComment = (comment: Comment, getReplies: (id: number) => Comment[], depth = 0) => {
    const replies = getReplies(comment.id);
    const canDeleteComment = user && (user.id === comment.user_id || isAdmin);

    return (
      <div key={comment.id} className={`comment ${depth > 0 ? "comment-reply" : ""}`}>
        <div className="comment-header">
          <span className="comment-author">@{comment.username}</span>
          <span className="comment-date">{formatDate(comment.created_at)}</span>
        </div>
        <p className="comment-body">{comment.body}</p>
        <div className="comment-actions">
          {user && (
            <button
              className="comment-action-btn"
              onClick={() => {
                setReplyingTo(replyingTo === comment.id ? null : comment.id);
                setReplyText("");
              }}
            >
              Reply
            </button>
          )}
          {canDeleteComment && (
            <button
              className="comment-action-btn comment-delete-btn"
              onClick={() => handleDeleteComment(comment.id)}
            >
              Delete
            </button>
          )}
        </div>

        {replyingTo === comment.id && (
          <div className="reply-form">
            <textarea
              className="comment-input"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={`Reply to @${comment.username}...`}
              rows={2}
            />
            <div className="comment-form-actions">
              <button
                className="btn-small"
                onClick={() => setReplyingTo(null)}
              >
                Cancel
              </button>
              <button
                className="btn-small btn-primary-small"
                onClick={() => handleSubmitComment(comment.id)}
                disabled={submittingComment || !replyText.trim()}
              >
                Reply
              </button>
            </div>
          </div>
        )}

        {replies.length > 0 && (
          <div className="comment-replies">
            {replies.map(reply => renderComment(reply, getReplies, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="story-page loading">Loading...</div>;
  }

  if (error || !story) {
    return (
      <div className="story-page error">
        <button className="back-btn" onClick={onBack}>
          &larr; Back
        </button>
        <p>{error || "Story not found"}</p>
      </div>
    );
  }

  const htmlContent = marked(story.body, { breaks: true }) as string;
  const wordCount = editBody.trim().split(/\s+/).filter(w => w.length > 0).length;
  const { rootComments, getReplies } = buildCommentTree(comments);

  return (
    <div className="story-page">
      <button className="back-btn" onClick={onBack}>
        &larr; Back
      </button>

      <article className="story-article">
        <header className="story-header">
          {isEditing ? (
            <input
              type="text"
              className="edit-title-input"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Title"
            />
          ) : (
            <h1 className="story-title">{story.title}</h1>
          )}
          <div className="story-meta">
            <span className="story-author">@{story.username}</span>
            <button
              className={`like-btn ${hasLiked ? "liked" : ""}`}
              onClick={handleLike}
            >
              <span className="heart">{hasLiked ? "♥" : "♡"}</span>
              <span>{likeCount}</span>
            </button>
          </div>
        </header>

        <div className="reading-controls">
          <button
            className="font-btn"
            onClick={() => changeFontSize(-0.1)}
            title="Decrease font size"
          >
            A-
          </button>
          <button
            className="font-btn"
            onClick={() => changeFontSize(0.1)}
            title="Increase font size"
          >
            A+
          </button>
          {isOwner && !isEditing && (
            <button className="edit-btn" onClick={() => setIsEditing(true)}>
              Edit
            </button>
          )}
          {canDelete && !isEditing && (
            <button className="delete-btn" onClick={handleDelete}>
              Delete
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="edit-form">
            <textarea
              className="edit-body-input"
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              placeholder="Story body (Markdown supported)"
              rows={20}
            />
            <div className="edit-footer">
              <span className={`word-count ${wordCount > 3000 ? "over-limit" : ""}`}>
                {wordCount} / 3000 words
              </span>
              <div className="edit-actions">
                <button
                  className="cancel-btn"
                  onClick={() => {
                    setIsEditing(false);
                    setEditTitle(story.title);
                    setEditBody(story.body);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleSave}
                  disabled={saving || wordCount > 3000}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="story-body"
            style={{ fontSize: `${fontSize}rem` }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        )}
      </article>

      {/* Comments Section */}
      <section className="comments-section">
        <h2 className="comments-header">Comments</h2>

        {/* New comment form */}
        <div className="new-comment-form">
          <textarea
            className="comment-input"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={user ? "Write a comment..." : "Login to comment"}
            rows={3}
            disabled={!user}
          />
          <div className="comment-form-actions">
            <button
              className="btn-primary"
              onClick={() => handleSubmitComment()}
              disabled={submittingComment || !newComment.trim() || !user}
            >
              {submittingComment ? "Posting..." : "Post Comment"}
            </button>
          </div>
        </div>

        {/* Comments list */}
        <div className="comments-list">
          {rootComments.length === 0 ? (
            <p className="no-comments">No comments yet. Be the first to share your thoughts!</p>
          ) : (
            rootComments.map(comment => renderComment(comment, getReplies))
          )}
        </div>
      </section>
    </div>
  );
}
